/* eslint-disable no-underscore-dangle */
// Poll for whether there has been any site activity every X seconds.
// The server does not get polled if there has been mouse/keyboard input
import axios from 'axios';
import { metaValue } from 'helpers/dom-helper';
import { nextFrame } from 'helpers/timing';

export default class SessionPoller {
  constructor() {
    this.sessionTimeoutLength = 60;
    this.lastActive = new Date().valueOf();
    this.sessionTimeoutPollFrequency = 180;
    this.remainingSession = this.sessionTimeoutLength * 60; // number of seconds for the session timeout set in devise.rb
    this._countdownInterval = null;
    this.countdownTime = this.remainingSession;
    this.modalActive = false;
    this.polling = false;
  }

  set countdownInterval(val) {
    this._countdownInterval = val;
  }

  get countdownInterval() {
    return this._countdownInterval;
  }

  init = async () => {
    // The page reports the session's remaining seconds (layouts/_session_timeout_meta), so starting needs no request:
    // it used to be a /ka on every page load, public pages included. Signed out pages and development render no
    // meta, and the poller stays off there.
    const remaining = metaValue('session-timeout-remaining');
    if (remaining === null) return;

    this.remainingSession = Number(remaining);
    this.countdownTime = Number(remaining);
    ['mousemove', 'scroll', 'click', 'keydown'].forEach((activity) => {
      document.addEventListener(
        activity,
        () => {
          this.lastActive = Date.now();
        },
        { capture: true }
      );
    });
    this.polling = true;
    this.poll();
  };

  poll = () => {
    this.engageCountdown();
    setTimeout(this.pollForSessionTimeout, this.sessionTimeoutPollFrequency * 1000);
  };

  pollForSessionTimeout = () => {
    // The server only hears from a user who makes requests, and typing in a long form makes none. If they have
    // been active since the last poll, tell the server so, rather than waiting for the final seconds of the
    // session: a keepalive timed for the expiry boundary arrives after Devise has already signed them out.
    if (Date.now() - this.lastActive < this.sessionTimeoutPollFrequency * 1000 && !this.modalActive) {
      this.sendKeepAliveRequest();
    } else {
      this.checkTimeoutRequest();
    }
    this.poll();
  };

  checkTimeoutRequest = () => {
    // in development 304 are treated like errors by axios, this negates that
    axios
      .get('/check_session_timeout', {
        validateStatus: (status) => status === 304 || (status >= 200 && status < 300),
      })
      .then(async (response) => {
        // if the remaining valid time for the current user session is less than or equals to 0 seconds.
        if (response.status === 200 && response.data <= 0) {
          await this.handleExpiredSession();
        } else {
          this.remainingSession = response.data;
          this.countdownTime = response.data;
          this.engageCountdown();
          // If they have not been active and it's less than 360 seconds, show them a message
          if (this.remainingSession < 360 && !this.modalActive) {
            this.handleMessage();
          } else if (this.remainingSession > 360 && this.modalActive) {
            // If timeout is reset then close the modal
            $('#sessionExpiringWarning').modal('hide');
          }
        }
      })
      .catch(async (error) => {
        // A network failure has no `response`. Only a 401 means the user has been signed out.
        if (error.response?.status === 401) await this.handleExpiredSession();
      });
  };

  // Lets `session-timeout` listeners (the auto-save controller) store a final draft before the page is replaced.
  // Dispatch only returns when the listeners have been invoked, and storing a draft is an IndexedDB write that
  // takes several turns of the event loop, so listeners hand back a promise through `waitUntil` and we wait for
  // it. A listener that hangs must not strand the user on a dead page, hence the timeout.
  //
  // A listener can also refuse the redirect with `preventDefault()`. The auto-save controller does while a Save is
  // held and this browser could not store a copy: the page is then the only place the work exists. The countdown
  // is stopped (it re-checks every second once it has run out) and the page is left alone; the next poll asks
  // again, and the next Save checks the session itself.
  handleExpiredSession = async () => {
    const pending = [];
    const mayRedirect = document.body.dispatchEvent(
      new CustomEvent('session-timeout', {
        bubbles: true,
        cancelable: true,
        detail: { waitUntil: (promise) => pending.push(promise) },
      })
    );
    const settled = Promise.allSettled(pending);
    if (!mayRedirect) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
      return;
    }

    await Promise.race([
      settled,
      new Promise((resolve) => {
        setTimeout(resolve, 2000);
      }),
    ]);
    await this.resumeSession();
  };

  resumeSession = async () => {
    // Give some time for the `session-timeout` listeners to process the event.
    await nextFrame();
    // Set this cookie to trigger a different message on the login screen.
    // Instead of 'You need to sign in or sign up before continuing.'
    // it will display
    // 'Your session has timed out.'
    document.cookie = `resumeAfterTimeout=true; path=/signin`;
    // Trigger the login process by revisiting the current url.
    // The login process will return us back here.
    // Preserve the query string so Devise's stored_location_for round-trip
    // lands us back on the byte-identical URL — the auto-save controller
    // keys new-record drafts by pathname + search, so dropping the query
    // string here orphans the user's in-progress draft in IndexedDB.
    window.location.href = window.location.pathname + window.location.search;
  };

  engageCountdown = () => {
    if (!this.countdownInterval) this.countdownInterval = setInterval(this.updateTimeRemainingMessage, 1000);
  };

  sendKeepAliveRequest = () =>
    new Promise((resolve) => {
      // Nothing resolves this promise while the warning modal is up, so only call it where that is expected.
      if (this.modalActive) return;

      axios
        .get('/ka')
        .then((response) => {
          this.remainingSession = response.data.remaining;
          this.countdownTime = response.data.remaining;
          resolve(response);
        })
        .catch(async (error) => {
          // Resolve either way, so a request that fails (offline, a 500) leaves nothing waiting on it.
          resolve(null);
          // Only polls send this (init reads the page's meta instead), so it never runs for a visitor on a public page.
          if (this.polling && error.response?.status === 401) await this.handleExpiredSession();
        });
    });

  updateTimeRemainingMessage = () => {
    this.countdownTime -= 1;
    if (this.countdownTime < 0) {
      this.checkTimeoutRequest();
      return;
    }
    if (!document.getElementById('remainingTime')) return;
    if (this.countdownTime <= 10) {
      document.getElementById('remainingTime').classList.add('text-danger');
    }
    document.getElementById('remainingTime').textContent = `${this.countdownTime} second${
      this.countdownTime === 1 ? '' : 's'
    }`;
  };

  handleMessage = () => {
    if (!document.getElementById('sessionExpiringWarning')) {
      const msg = `
          <div class="modal fade" id="sessionExpiringWarning" tabindex="-1" role="dialog" aria-labelledby="sessionExpiringWarning" aria-hidden="true">
            <div class="modal-dialog" role="document">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="sessionExpiringWarningLabel">You will be logged out in...</h5>
                  <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <h1 id="remainingTime" class="text-center mt-0">${this.countdownTime} seconds</h1>
                  <p class="fs-3"><em>Ambiki automatically signs you out after 60 minutes of inactivity to keep your data secure.</em></p>
                </div>
                <div class="modal-footer">
                  <a class="btn btn-light" itemprop="url" rel="nofollow" data-method="delete" href="/signout">
                    <i class="bi bi-box-arrow-right"></i> Sign out now</a>
                  <button type="button" class="btn btn-primary" data-dismiss="modal">Stay logged in</button>
                </div>
              </div>
            </div>
          </div>`;
      document.body.insertAdjacentHTML('beforeend', msg);
      if (typeof $('#sessionExpiringWarning').modal === 'function') {
        $('#sessionExpiringWarning').on('show.bs.modal', () => {
          this.modalActive = true;
          document.getElementById('remainingTime').textContent = '...';
        });
        $('#sessionExpiringWarning').on('hidden.bs.modal', () => {
          this.modalActive = false;
          this.sendKeepAliveRequest();
          document.getElementById('remainingTime').classList.remove('text-danger');
        });
      }
    }
    $('#sessionExpiringWarning').modal('show');
  };
}
